import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Copy, Download, Facebook, Linkedin, Instagram, Twitter, Share2 } from "lucide-react";

// Reusable social share row. Pass either a ready public `url` or a `publish`
// async callback that returns a public absolute URL (used for base64 assets).
export function ShareBar({ url = "", publish, caption = "", downloadName = "penhaus-asset", testidPrefix = "share" }) {
  const [pub, setPub] = useState(url);
  const [busy, setBusy] = useState(false);

  const ensure = async () => {
    if (pub) return pub;
    if (!publish) throw new Error("no url");
    setBusy(true);
    try {
      const u = await publish();
      setPub(u);
      return u;
    } finally {
      setBusy(false);
    }
  };

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(caption || ""); toast.success("Caption copied to clipboard"); }
    catch { toast.error("Couldn't copy caption"); }
  };

  const doDownload = async () => {
    try {
      const u = await ensure();
      const a = document.createElement("a");
      a.href = u; a.download = downloadName; a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Couldn't prepare the file"); }
  };

  const openNetwork = (network) => async () => {
    let u;
    try { u = await ensure(); } catch { toast.error("Couldn't prepare share link"); return; }
    const enc = encodeURIComponent(u);
    const cap = encodeURIComponent(caption || "");
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc}`,
      linkedin: `https://www.linkedin.com/sharing/share-offer?url=${enc}`,
      x: `https://twitter.com/intent/tweet?text=${cap}&url=${enc}`,
    };
    window.open(links[network], "_blank", "noopener,noreferrer,width=660,height=640");
  };

  const instagram = async () => {
    await doDownload();
    try { await navigator.clipboard.writeText(caption || ""); } catch { /* ignore */ }
    toast.info("Downloaded + caption copied — open Instagram and upload to post.");
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`${testidPrefix}-bar`}>
      <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1.5"><Share2 className="h-3.5 w-3.5" /> Share this</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={openNetwork("facebook")} disabled={busy} data-testid={`${testidPrefix}-facebook`} className="text-[#1877F2] border-slate-200"><Facebook className="h-4 w-4 mr-1.5" /> Facebook</Button>
        <Button variant="outline" size="sm" onClick={openNetwork("linkedin")} disabled={busy} data-testid={`${testidPrefix}-linkedin`} className="text-[#0A66C2] border-slate-200"><Linkedin className="h-4 w-4 mr-1.5" /> LinkedIn</Button>
        <Button variant="outline" size="sm" onClick={openNetwork("x")} disabled={busy} data-testid={`${testidPrefix}-x`} className="text-slate-900 border-slate-200"><Twitter className="h-4 w-4 mr-1.5" /> X</Button>
        <Button variant="outline" size="sm" onClick={instagram} disabled={busy} data-testid={`${testidPrefix}-instagram`} className="text-[#E1306C] border-slate-200"><Instagram className="h-4 w-4 mr-1.5" /> Instagram</Button>
        {caption ? <Button variant="outline" size="sm" onClick={copyCaption} data-testid={`${testidPrefix}-copy-caption`} className="border-slate-200"><Copy className="h-4 w-4 mr-1.5" /> Copy caption</Button> : null}
        <Button variant="outline" size="sm" onClick={doDownload} disabled={busy} data-testid={`${testidPrefix}-download`} className="border-slate-200"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
      </div>
    </div>
  );
}
